import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Decoration } from '../../../../models/configurators-2d/applications/fence/decoration';
import { FenceService } from '../../../../services/configurators-2d/applications/fence.service';
import { Configuration } from '../../../../models/configurators-2d/applications/fence/configuration';
import { from, Observable, tap } from 'rxjs';
import { distinct } from '../../../../services/helpers/utils';
import { getMaxHeight } from '../../../../services/helpers/fence-helper';
import { DecorType } from 'src/app/models/configurators-2d/applications/fence/decor-type';

@Injectable({
  providedIn: 'root',
})
export class DecorationStateService {
  /**
   * Liste des décors
   * @private
   */
  private decorations: WritableSignal<Decoration[]> = signal([]);

  /**
   * Indique si le service est entrain de faire des changements
   */
  loading: WritableSignal<boolean> = signal(false);

  /**
   * Liste des lames décoratives
   */
  decorativeBoards: Signal<Decoration[]> = computed(() => {
    return this.decorations().filter(d => d.decorType === DecorType.DecorativeBoard);
  });

  /**
   * Liste des décorations (non panneaux)
   */
  decors: Signal<Decoration[]> = computed(() => {
    return this.decorations().filter(d => d.decorType === DecorType.Decor);
  });

  /**
   * Liste des panneaux
   */
  panels: Signal<Decoration[]> = computed(() => {
    return this.decorations().filter(d => d.decorType === DecorType.Panel);
  });

  private readonly fenceService = inject(FenceService);

  /**
   * Requête l'API et défini la liste des décors disponibles
   * @param configuration
   */
  setList(configuration: Configuration): Observable<Decoration[]> {
    this.loading.set(true);

    let decorationIdsSelected = configuration.horizontalDecorations.map(d => d.id);
    decorationIdsSelected.push(...configuration.sides.flatMap(s => s.verticalDecorations.map(d => d.id)));
    decorationIdsSelected.push(...configuration.horizontalPanels.map(d => d.id));
    decorationIdsSelected.push(...configuration.sides.flatMap(s => s.verticalPanels.map(d => d.id)));
    decorationIdsSelected.push(...configuration.horizontalDecorativeBoards.map(d => d.id));
    decorationIdsSelected.push(...configuration.sides.flatMap(s => s.verticalDecorativeBoards.map(d => d.id)));

    decorationIdsSelected = distinct(decorationIdsSelected);

    return from(
      this.fenceService.getDecorations(
        configuration.postType.id,
        configuration.configurationInstallations.map(i => i.installationId),
        getMaxHeight(configuration),
        decorationIdsSelected
      )
    ).pipe(
      tap((res: Decoration[]) => {
        this.decorations.set(res);
      })
    );
  }
}
